import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { customerCreateSchema, type CustomerCreate } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { FieldError, Input, Label, Textarea } from '../../components/ui/field';
import { api } from '../../shared/api';
import { copy } from '../../shared/i18n';

export function CustomerCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<CustomerCreate>({
    resolver: zodResolver(customerCreateSchema),
    defaultValues: { firstName: '', lastName: '', phone: '', email: '', address: '', emergencyContact: '', notes: '' },
  });
  const createCustomer = useMutation({
    mutationFn: api.customers.create,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate('/admin/customers');
    },
  });

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">{copy.customers.newCustomer}</h1>
        <p className="mt-1 text-sm text-slate-500">{copy.customers.body}</p>
      </div>
      <Card>
        <CardHeader><h2 className="text-sm font-semibold">{copy.customers.create}</h2></CardHeader>
        <CardContent>
          <form className="grid gap-3" onSubmit={form.handleSubmit((values) => createCustomer.mutate(values))}>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="firstName">{copy.customers.firstName}</Label>
                <Input id="firstName" {...form.register('firstName')} />
                <FieldError message={form.formState.errors.firstName?.message} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="lastName">{copy.customers.lastName}</Label>
                <Input id="lastName" {...form.register('lastName')} />
                <FieldError message={form.formState.errors.lastName?.message} />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="phone">{copy.common.phone}</Label>
                <Input id="phone" {...form.register('phone')} />
                <FieldError message={form.formState.errors.phone?.message} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="email">{copy.common.email}</Label>
                <Input id="email" type="email" {...form.register('email')} />
                <FieldError message={form.formState.errors.email?.message} />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="address">{copy.customers.address}</Label>
              <Input id="address" {...form.register('address')} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="emergencyContact">{copy.customers.emergencyContact}</Label>
              <Input id="emergencyContact" {...form.register('emergencyContact')} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="notes">{copy.customers.notes}</Label>
              <Textarea id="notes" {...form.register('notes')} />
            </div>
            {createCustomer.isError ? <p className="text-sm text-rose-600">{createCustomer.error.message}</p> : null}
            <Button type="submit" disabled={createCustomer.isPending}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {copy.customers.create}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
